import {
  all,
  cancel,
  delay,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
  type SagaReturnType,
} from 'redux-saga/effects';

import { api } from 'src/api';
import { EXPERIMENT_NAMES } from 'src/components/Experiment';
import { getIsHotelOnly, searchTypeSelector } from 'src/store/view/selectors';
import { getIsFeatureHasAnOption } from 'src/utils/featureToggle';

import {
  ACTUALIZATION_TIMEOUT,
  ACTUALIZE_PACKAGE_FAIL,
  SET_ANALYTICS,
  START_ACTUALIZE_PACKAGE,
} from '../constants/actualization';

import {
  actualizePackageAction,
  actualizePackageFail,
  actualizePackageSuccess,
  setFrontErrorAction,
  stopActualizationProcessAction,
} from '../actions/actualization';

import {
  getArchivedAt,
  getLabels,
  getPackage,
  getPackageId,
} from '../selectors/package';

import { getVisaType } from '../domains/visa';

import { getHotelAnalytics } from '../selectors/hotel';

import {
  getIsActualizationProcess,
  selectCurrentPrice,
  selectIsPriceChanged,
} from '../selectors/actualization';

import { getIsOrderPrebuildFetching, getOrderFlight } from '../selectors/order';

import {
  prebuildOrderAction,
  prebuildOrderSuccess,
  selectFlight,
} from '../actions/order';
import { similarPackagesStartAction } from '../actions/similar-packages';

import {
  getBookingId,
  getIsPrebookInProgress,
  getIsPrebookSuccess,
  onPrebookSuccess,
  onPrebookTryFailed,
  onTryPrebook,
} from '../domains/package-prebook';

import { fetchAlternatives } from '../../CheckoutRoutes/PackageCheckout/store/actualize-alternatives/slice';
import { flightsForAnalyticsSelector } from '../selectors/analytics';
import { getIsHomePath } from '../selectors/router';

import { fligthsFetchingDoneAction } from '../../CheckoutRoutes/PackageCheckout/store/flights/slice';
import { actualizationDTO } from '../actualization/actualizationDTO';
import {
  hidePriceChanged,
  setCurrentPrice,
  setPreviousPrice,
  showPriceChanged,
} from '../reducers/actualization';
import { selectNewPrice, selectOldPrice } from '../selectors/price';

// For analytics
function* setAnalytics() {
  const flights: SagaReturnType<typeof flightsForAnalyticsSelector> =
    yield select(flightsForAnalyticsSelector);
  const visaType: SagaReturnType<typeof getVisaType> = yield select(
    getVisaType,
  );
  const hotel: SagaReturnType<typeof getHotelAnalytics> = yield select(
    getHotelAnalytics,
  );
  const hotelLabels: SagaReturnType<typeof getLabels> = yield select(getLabels);
  const packageObj: SagaReturnType<typeof getPackage> = yield select(
    getPackage,
  );
  const searchType: SagaReturnType<typeof searchTypeSelector> = yield select(
    searchTypeSelector,
  );

  const labels = hotelLabels
    ? hotelLabels.map((label) => label.id).join(';')
    : null;

  window.HOTEL = hotel;
  window.VISA = visaType;
  window.PACKAGE = {
    ...window.PACKAGE,
    ...packageObj,
    labels,
    search_type: searchType,
  };
  window.FLIGHTS = {
    flight_options: JSON.stringify(flights).replace(/;|,/g, '|'),
  };
}

/**
 * Запускает актуализацию
 */
function* startActualization() {
  const isArchived: SagaReturnType<typeof getArchivedAt> = yield select(
    getArchivedAt,
  );

  if (!isArchived) {
    yield put(actualizePackageAction());
  }
}

/**
 * Перезапускает актуализацию через таймаут
 */
function* restartActualization() {
  const isArchived: SagaReturnType<typeof getArchivedAt> = yield select(
    getArchivedAt,
  );

  if (isArchived) {
    yield cancel();
  }

  yield delay(ACTUALIZATION_TIMEOUT);

  const continueActualization: SagaReturnType<
    typeof getIsActualizationProcess
  > = yield select(getIsActualizationProcess);
  if (continueActualization) {
    yield put(actualizePackageAction());
  }
}

function* handleActualization({
  payload,
}: ReturnType<typeof actualizePackageAction>) {
  const lastUpdated = Date.now();

  try {
    const packageId: SagaReturnType<typeof getPackageId> = yield select(
      getPackageId,
    );

    const selectedFlightId: SagaReturnType<typeof getOrderFlight> =
      yield select(getOrderFlight);
    const bookingId: SagaReturnType<typeof getBookingId> = yield select(
      getBookingId,
    );
    const isPrebookSuccess: SagaReturnType<typeof getIsPrebookSuccess> =
      yield select(getIsPrebookSuccess);

    const force = payload?.force;

    const params = {
      package_id: packageId,
      flight_pair_id: selectedFlightId,
      booking_id: isPrebookSuccess ? bookingId : undefined,
      force,
    };

    const response = yield api.getPackagesActualize(params);
    if (!response) {
      throw new Error('Network error');
    }

    const data = actualizationDTO(response);

    const {
      rotten,
      package: { ordered },
      success,
      exclude_operator,
      exception,
      show_modal_customer,
    } = data;

    const failureData = {
      ordered,
      lastUpdated,
      rotten,
      exclude_operator,
      exception,
    };

    const successData = {
      data,
      lastUpdated,
      show_modal_customer,
    };

    /**
     * последняя актуализация с флагом force, если последняя актуализация сфейлилась
     * все равно создать заказ
     * Задача: https://leveltravel.atlassian.net/browse/LT-34069
     */

    if (success || force) {
      yield put(actualizePackageSuccess(successData));
    } else {
      if (selectedFlightId) {
        yield put(setFrontErrorAction('actualize_flight_error'));
      } else {
        yield put(setFrontErrorAction('actualize_error'));
      }

      yield put(actualizePackageFail(failureData));
    }
  } catch (error) {
    if (error instanceof Error && error.message === 'Network error') {
      yield put(setFrontErrorAction('network_error'));
    } else {
      yield put(setFrontErrorAction('data_parsing_error'));
    }
    // eslint-disable-next-line no-console
    console.error('Не удалось актуализировать пакет', error);
    yield put(actualizePackageFail({ lastUpdated }));
  }
}

/**
 * Обработка актуализации пакета
 */
function* handleActualizationSuccess() {
  const isPriceChanged: SagaReturnType<typeof selectIsPriceChanged> =
    yield select(selectIsPriceChanged);
  const orderFlight: SagaReturnType<typeof getOrderFlight> = yield select(
    getOrderFlight,
  );

  const isPrebookInProgress: SagaReturnType<typeof getIsPrebookInProgress> =
    yield select(getIsPrebookInProgress);

  if (isPrebookInProgress) {
    if (isPriceChanged) {
      yield put(onPrebookSuccess());
    } else {
      yield put(onTryPrebook());
    }
  }

  if (orderFlight) {
    yield put(prebuildOrderAction());
  }

  yield setAnalytics();

  const isHomePath: SagaReturnType<typeof getIsHomePath> = yield select(
    getIsHomePath,
  );
  if (isHomePath) {
    yield put(fetchAlternatives());
  }

  const { NAME, OPTIONS } = EXPERIMENT_NAMES.ACTUALIZE_ALTERNATIVES;
  const allowAlternatives = getIsFeatureHasAnOption(NAME, OPTIONS.ON);

  if (!allowAlternatives || !isHomePath) {
    yield restartActualization();
  }
}

function* handleActualizationFail() {
  const isPrebookInProgress: SagaReturnType<typeof getIsPrebookInProgress> =
    yield select(getIsPrebookInProgress);
  if (isPrebookInProgress) {
    yield restartActualization();
  } else {
    const isHomePath: SagaReturnType<typeof getIsHomePath> = yield select(
      getIsHomePath,
    );
    const isHotelOnly: SagaReturnType<typeof getIsHotelOnly> = yield select(
      getIsHotelOnly,
    );
    const { NAME, OPTIONS } = EXPERIMENT_NAMES.ACTUALIZE_ALTERNATIVES;
    const allowAlternatives = getIsFeatureHasAnOption(NAME, OPTIONS.ON);
    if (allowAlternatives && isHomePath && !isHotelOnly) {
      yield put(fetchAlternatives());
    } else {
      yield put(stopActualizationProcessAction());
      yield put(similarPackagesStartAction());
    }
  }
}

function* handleFlightsFetchingDone() {
  /**
   * У нас после актуализации запускается пребилд,
   * который обновляет цену. Нам нужно дождаться его ответа,
   * чтобы избежать гонку данных
   */
  if (yield select(getIsOrderPrebuildFetching))
    yield take(prebuildOrderSuccess);
  const firstPrice: SagaReturnType<typeof selectOldPrice> = yield select(
    selectOldPrice,
  );
  const newPrice: SagaReturnType<typeof selectNewPrice> = yield select(
    selectNewPrice,
  );
  // На первом шаге чекаута = null, дальше является буффером для цены
  const previousPrice: SagaReturnType<typeof selectCurrentPrice> = yield select(
    selectCurrentPrice,
  );

  yield put(setPreviousPrice(previousPrice || firstPrice));
  yield put(setCurrentPrice(newPrice));

  /**
   * firstPrice - старая цена из первой актуализации, далее
   * previousPrice - буффер для новой цены
   */
  if (Math.abs(newPrice - (previousPrice || firstPrice)) > 1) {
    yield put(showPriceChanged());
  } else yield put(hidePriceChanged());
}

export default function* actualizationWatcher() {
  yield all([
    yield takeEvery(
      [START_ACTUALIZE_PACKAGE, onPrebookTryFailed, selectFlight],
      startActualization,
    ),
    yield takeLatest(actualizePackageAction, handleActualization),
    yield takeLatest(actualizePackageSuccess, handleActualizationSuccess),
    yield takeLatest(ACTUALIZE_PACKAGE_FAIL, handleActualizationFail),
    yield takeLatest(SET_ANALYTICS, setAnalytics),
    yield takeEvery(fligthsFetchingDoneAction.type, handleFlightsFetchingDone),
  ]);
}
